/**
 * ChatInput — text input with send button.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useState } from "react";
import { StyleSheet, TextInput, TouchableOpacity, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { colors, fonts, radius, spacing } from "@/lib/theme";

interface ChatInputProps {
    onSend: (text: string) => void;
    disabled?: boolean;
    placeholder?: string;
}

export function ChatInput({ onSend, disabled, placeholder }: ChatInputProps) {
    const [text, setText] = useState("");
    const insets = useSafeAreaInsets();

    const handleSend = () => {
        const trimmed = text.trim();
        if (!trimmed || disabled) {
            return;
        }
        onSend(trimmed);
        setText("");
    };

    const canSend = text.trim().length > 0 && !disabled;

    return (
        <View
            style={[
                styles.container,
                {
                    paddingBottom: Math.max(insets.bottom, spacing.sm)
                }
            ]}
        >
            <TextInput
                style={styles.input}
                value={text}
                onChangeText={setText}
                placeholder={placeholder ?? "Type a message..."}
                placeholderTextColor={colors.textTertiary}
                multiline
                maxLength={2000}
                editable={!disabled}
                onSubmitEditing={handleSend}
                blurOnSubmit={false}
            />
            <TouchableOpacity
                style={[styles.sendBtn, !canSend && styles.sendBtnDisabled]}
                onPress={handleSend}
                disabled={!canSend}
                activeOpacity={0.7}
            >
                <MaterialCommunityIcons
                    name="arrow-up"
                    size={20}
                    color={canSend ? "#FFFFFF" : colors.textTertiary}
                />
            </TouchableOpacity>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        alignItems: "flex-end",
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        backgroundColor: colors.surface,
        borderTopWidth: 1.5,
        borderTopColor: colors.border,
        gap: spacing.sm
    },
    input: {
        flex: 1,
        backgroundColor: colors.background,
        borderRadius: radius.full,
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.sm,
        fontSize: 15,
        fontFamily: fonts.medium,
        color: colors.textPrimary,
        maxHeight: 100,
        borderWidth: 2,
        borderColor: colors.border
    },
    sendBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: colors.teal400,
        alignItems: "center",
        justifyContent: "center"
    },
    sendBtnDisabled: {
        backgroundColor: colors.gray200
    }
});
